import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../app/providers/AuthProvider";
import {
  setHasMultipleAccounts,
  setSelectedAccountId as persistSelectedAccountId,
} from "../../../shared/lib/auth/accountContext";
import { authRoutes } from "../config/authRoutes";
import {
  chooseAccount,
  getSelectableAccounts,
} from "../api/accountSelection";

export function useAccountSelection() {
  const navigate = useNavigate();
  const { updateAuthenticatedUser, user } = useAuth();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [error, setError] = useState("");

  const hasAccounts = accounts.length > 0;
  const accountsLabel = useMemo(() => {
    const count = accounts.length || Number(user?.accountsCount || 0);
    return `${count} ${count === 1 ? "conta encontrada" : "contas encontradas"}`;
  }, [accounts.length, user?.accountsCount]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const result = await getSelectableAccounts();
      setAccounts(result.accounts);
      setHasMultipleAccounts(user?.email, result.accounts.length > 1);
    } catch (loadError) {
      setError(loadError?.message || "Não foi possível carregar suas contas.");
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  async function selectAccount(accountId) {
    if (!accountId || refreshing) return;

    setSelectedAccountId(accountId);
    setRefreshing(true);
    setError("");

    try {
      const result = await chooseAccount(accountId);
      const selectedId = result.selected_account_id;
      if (!selectedId) {
        throw new Error("Contrato de seleção de conta inválido.");
      }
      persistSelectedAccountId(user?.email, selectedId);
      setHasMultipleAccounts(user?.email, accounts.length > 1);

      const nextUser = updateAuthenticatedUser({
        ...user,
        accountId: selectedId,
        accountSelectionRequired: false,
        accountsCount: accounts.length || user?.accountsCount || 0,
      });

      if (!nextUser?.accountId || nextUser?.accountSelectionRequired) {
        throw new Error("Não foi possível confirmar a conta selecionada. Tente novamente.");
      }

      navigate(authRoutes.dashboard, { replace: true, state: { user: nextUser } });
    } catch (selectError) {
      setError(selectError?.message || "Não foi possível acessar esta conta.");
      setSelectedAccountId("");
    } finally {
      setRefreshing(false);
    }
  }

  return {
    accounts,
    accountsLabel,
    error,
    hasAccounts,
    loading,
    refreshing,
    reload: loadAccounts,
    selectAccount,
    selectedAccountId,
  };
}
